import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Download, FileText, Search, FileSpreadsheet, Trash2, Filter } from "lucide-react";
import { toast } from "sonner";

import { logoColor as LOGO_URL } from "@/lib/logos";

export default function TecnicoHistorial() {
  const { api, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    fecha_desde: "", fecha_hasta: "", tipo: "", estado: "", cliente: "", sucursal: "", tecnico: "",
  });

  useEffect(() => { loadReports(); }, []);

  const buildParams = (f) => {
    const p = new URLSearchParams();
    if (f.fecha_desde) p.set("fecha_desde", f.fecha_desde);
    if (f.fecha_hasta) p.set("fecha_hasta", f.fecha_hasta);
    if (f.tipo && f.tipo !== "todos") p.set("tipo", f.tipo);
    if (f.estado && f.estado !== "todos") p.set("estado", f.estado);
    if (f.cliente) p.set("cliente", f.cliente);
    if (f.sucursal) p.set("sucursal", f.sucursal);
    if (f.tecnico) p.set("tecnico", f.tecnico);
    return p.toString();
  };

  const loadReports = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/api/maintenance?${buildParams(filters)}&limit=100`);
      setReports(r.data);
    } catch { toast.error("Error al cargar reportes"); }
    finally { setLoading(false); }
  };

  const applyFilters = () => { loadReports(); };

  const clearFilters = () => {
    setFilters({ fecha_desde: "", fecha_hasta: "", tipo: "", estado: "", cliente: "", sucursal: "", tecnico: "" });
    setTimeout(() => loadReports(), 50);
  };

  const downloadPdf = async (reportId, folio) => {
    try {
      const r = await api.get(`/api/maintenance/${reportId}/pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      const disposition = r.headers["content-disposition"];
      const filename = disposition ? disposition.split("filename=")[1]?.replace(/"/g, "") : `${folio}.pdf`;
      a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Error al descargar PDF"); }
  };

  const exportExcel = async () => {
    try {
      const r = await api.get(`/api/maintenance/export/excel?${buildParams(filters)}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "mantenciones_odontofix.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch { toast.error("Error al exportar"); }
  };

  const deleteReport = async (reportId, folio) => {
    if (!window.confirm(`¿Eliminar reporte ${folio}? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/api/maintenance/${reportId}`);
      toast.success("Reporte eliminado");
      loadReports();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al eliminar"); }
  };

  const uf = (f, v) => setFilters((p) => ({ ...p, [f]: v }));

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <img src={LOGO_URL} alt="" className="h-7" />
            <span className="text-sm font-semibold text-[#384091] truncate">Historial</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isAdmin && (
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={exportExcel}>
                <FileSpreadsheet className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Excel</span>
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
              <Filter className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Filtros</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/tecnico/dashboard")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-4">
        {/* Filters panel */}
        {showFilters && (
          <Card className="border-0 shadow-sm mb-4">
            <CardContent className="p-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><Label className="text-xs">Fecha desde</Label><Input type="date" value={filters.fecha_desde} onChange={(e) => uf("fecha_desde", e.target.value)} className="h-11 sm:h-9 text-xs" /></div>
                <div><Label className="text-xs">Fecha hasta</Label><Input type="date" value={filters.fecha_hasta} onChange={(e) => uf("fecha_hasta", e.target.value)} className="h-11 sm:h-9 text-xs" /></div>
                <div><Label className="text-xs">Tipo</Label>
                  <Select value={filters.tipo} onValueChange={(v) => uf("tipo", v)}>
                    <SelectTrigger className="h-11 sm:h-9 text-xs"><SelectValue placeholder="Todos" /></SelectTrigger>
                    <SelectContent><SelectItem value="todos">Todos</SelectItem><SelectItem value="preventiva">Preventiva</SelectItem><SelectItem value="correctiva">Correctiva</SelectItem></SelectContent>
                  </Select></div>
                <div><Label className="text-xs">Cliente</Label><Input placeholder="Buscar..." autoComplete="off" value={filters.cliente} onChange={(e) => uf("cliente", e.target.value)} className="h-11 sm:h-9 text-xs" /></div>
                <div><Label className="text-xs">Sucursal</Label><Input placeholder="Buscar..." autoComplete="off" value={filters.sucursal} onChange={(e) => uf("sucursal", e.target.value)} className="h-11 sm:h-9 text-xs" /></div>
                <div><Label className="text-xs">Técnico</Label><Input placeholder="Buscar..." autoComplete="off" value={filters.tecnico} onChange={(e) => uf("tecnico", e.target.value)} className="h-11 sm:h-9 text-xs" /></div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button size="sm" className="h-11 sm:h-9 bg-[#0FB4B7] text-white" onClick={applyFilters}><Search className="h-3.5 w-3.5 mr-1" /> Buscar</Button>
                <Button variant="outline" size="sm" className="h-11 sm:h-9" onClick={clearFilters}>Limpiar</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <p className="text-center text-gray-400 py-12">Cargando...</p>
        ) : reports.length === 0 ? (
          <p className="text-center text-gray-400 py-12">Sin reportes</p>
        ) : (
          <div className="space-y-2">
            {reports.map((r) => (
              <Card key={r.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <Link to={r.tipo === "correctiva" ? `/tecnico/correctiva/${r.id}` : `/tecnico/mantencion/${r.id}`} className="flex items-center gap-3 flex-1 min-w-0">
                    <FileText className="h-4 w-4 text-[#0FB4B7] flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {r.folio} — {r.cliente}
                        {r.tipo === "correctiva" ? (
                          <span className="ml-1.5 text-[10px] bg-[#384091] text-white px-1.5 py-0.5 rounded">MC</span>
                        ) : (
                          <span className="ml-1.5 text-[10px] bg-emerald-600 text-white px-1.5 py-0.5 rounded">MP</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500">{r.sucursal} · {r.marca_sillon} {r.modelo} · {r.fecha} · {r.tecnico_nombre}</p>
                    </div>
                  </Link>
                  <div className="flex items-center gap-1.5 flex-shrink-0 self-end sm:self-auto">
                    <Badge className={r.estado === "completado" ? "bg-emerald-50 text-emerald-700 border-0" : "bg-amber-50 text-amber-700 border-0"}>
                      {r.estado === "completado" ? "Completado" : "Borrador"}
                    </Badge>
                    {r.estado === "completado" && (
                      <Button variant="ghost" size="sm" onClick={() => downloadPdf(r.id, r.folio)} title="Descargar PDF">
                        <Download className="h-4 w-4" />
                      </Button>
                    )}
                    {isAdmin && (
                      <Button variant="ghost" size="sm" onClick={() => deleteReport(r.id, r.folio)} title="Eliminar" className="text-red-400 hover:text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
